import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
    DocumentSequenceForm,
    DocumentSequenceDetail,
    DocumentSequenceSummary,
} from '../../../types/document/DocumentSequenceTypes';

interface DocumentSequenceState {
    form: DocumentSequenceForm;
    detail: DocumentSequenceDetail | null;
    list: DocumentSequenceSummary[];
    activeList: DocumentSequenceSummary[];
    paginator: any,
    isSubmitting: boolean;
    formErrors: Record<string, string>;
    showRegistrationModal: boolean,
    showViewModal: boolean;
    showEditModal: boolean,
}

const initialForm: DocumentSequenceForm = {
  id: undefined,        // opcional si luego editas
  branchId: 0,          // ID inicial de sucursal
  documentTypeId: 0,    // ID inicial de tipo de documento
  prefix: '',           // prefijo inicial (ej. "DTE")
  currentNumber: null,     // correlativo inicial
};


const initialState: DocumentSequenceState = {
    form: initialForm,
    detail: null,
    list: [],
    activeList: [],
    paginator: null,
    isSubmitting: false,
    formErrors: {},
    showRegistrationModal: false,
    showViewModal: false,
    showEditModal: false
};

const documentSequenceSlice = createSlice({
    name: 'documentSequence',
    initialState,
    reducers: {
        setForm(state, action: PayloadAction<DocumentSequenceForm>) {
            state.form = { ...initialForm, ...action.payload };
        },
        updateFormField<K extends keyof DocumentSequenceForm>(
            state,
            action: PayloadAction<{ field: K; value: DocumentSequenceForm[K] }>
        ) {
            state.form[action.payload.field] = action.payload.value;
        },
        clearForm(state) {
            state.form = initialForm;
            state.formErrors = {};
        },
        setDetail(state, action: PayloadAction<DocumentSequenceDetail>) {
            state.detail = action.payload;
        },
        setList(state, action: PayloadAction<{ content: DocumentSequenceSummary[]; paginator: any }>) {
            state.list = action.payload.content;
            state.paginator = action.payload.paginator;
        },
        setActiveList(state, action: PayloadAction<DocumentSequenceSummary[]>) {
            state.activeList = action.payload;
        },
        setFormErrors(state, action: PayloadAction<Record<string, string>>) {
            state.formErrors = action.payload;
        },
        clearFormErrors(state) {
            state.formErrors = {};
        },
        setIsSubmitting(state, action: PayloadAction<boolean>) {
            state.isSubmitting = action.payload;
        },
        clearFilteredDocumentSequences(state) {
            state.list = [];
            state.paginator = null;
        },
        closeModalAndResetForm(state) {
            state.showRegistrationModal = false;
            state.form = initialForm;
            state.formErrors = {};
        },
        setShowRegistrationModal(state, action: PayloadAction<boolean>) {
            state.showRegistrationModal = action.payload;
        },
        setShowViewModal(state, action: PayloadAction<boolean>) {
            state.showViewModal = action.payload;
        },
        closeViewModalAndResetDetail(state) {
            state.showViewModal = false;
            state.detail = null;
        },
        setShowEditModal(state, action: PayloadAction<boolean>) {
            state.showEditModal = action.payload;
        },
        updateSequenceInList(
            state,
            action: PayloadAction<{ id: number; currentNumber: number }>
        ) {
            const { id, currentNumber } = action.payload;

            state.list = state.list.map((item) =>
                item.id === id
                    ? { ...item, currentNumber }
                    : item
            );
        },
    },
});

export const {
    setForm,
    updateFormField,
    clearForm,
    setDetail,
    setList,
    setActiveList,
    setFormErrors,
    clearFormErrors,
    setIsSubmitting,
    clearFilteredDocumentSequences,
    closeModalAndResetForm,
    setShowRegistrationModal,
    setShowViewModal,
    closeViewModalAndResetDetail,
    setShowEditModal,
    updateSequenceInList,
} = documentSequenceSlice.actions;

export default documentSequenceSlice.reducer;
